import { ApolloProvider } from '@apollo/client';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { portalApolloClient } from '@/modules/portal/portalApolloClient';
import { PortalAppointmentsPage } from '@/modules/portal/PortalAppointmentsPage';
import { PortalLoginPage } from '@/modules/portal/PortalLoginPage';
import { PortalPetDetailPage } from '@/modules/portal/PortalPetDetailPage';
import { PortalPetsPage } from '@/modules/portal/PortalPetsPage';
import { PortalProfilePage } from '@/modules/portal/PortalProfilePage';
import { PortalRequestAppointmentPage } from '@/modules/portal/PortalRequestAppointmentPage';
import { PortalShell } from '@/modules/portal/PortalShell';
import { RequireCustomerAuth } from '@/modules/portal/RequireCustomerAuth';

import { LoginPage } from '@/modules/auth/LoginPage';
import { RequireAuth } from '@/modules/auth/RequireAuth';
import { AuditPage } from '@/modules/audit/AuditPage';
import { CashShiftsPage } from '@/modules/cash-shifts/CashShiftsPage';
import { CashierPosPage } from '@/modules/cashier-pos/CashierPosPage';
import { ClinicalPendingPage } from '@/modules/clinical-records/ClinicalPendingPage';
import { AppointmentsPage } from '@/modules/appointments/AppointmentsPage';
import { HospitalizationPage } from '@/modules/hospitalization/HospitalizationPage';
import { OpenAccountsPage } from '@/modules/open-accounts/OpenAccountsPage';
import { OtpAdminPage } from '@/modules/otp-admin/OtpAdminPage';
import { PriceRulesPage } from '@/modules/pricing/PriceRulesPage';
import { CatalogIndexPage } from '@/modules/catalog/CatalogIndexPage';
import { CategoriesPage } from '@/modules/catalog/categories/CategoriesPage';
import { ProductsPage } from '@/modules/catalog/products/ProductsPage';
import { ServicesPage } from '@/modules/catalog/services/ServicesPage';
import { UnitsPage } from '@/modules/catalog/units/UnitsPage';
import { CustomerDetailPage } from '@/modules/customers-pets/CustomerDetailPage';
import { CustomersPage } from '@/modules/customers-pets/CustomersPage';
import { CustomerStatementPage } from '@/modules/customer-statement/CustomerStatementPage';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { DoctorPosPage } from '@/modules/doctor-pos/DoctorPosPage';
import { MedicalQueueBoardPage } from '@/modules/display-boards/MedicalQueueBoardPage';
import { WaitingRoomBoardPage } from '@/modules/display-boards/WaitingRoomBoardPage';
import { InventoryPage } from '@/modules/inventory/InventoryPage';
import { KitsPage } from '@/modules/inventory/KitsPage';
import { MovementsHistoryPage } from '@/modules/inventory/MovementsHistoryPage';
import { InvoicingPage } from '@/modules/invoicing/InvoicingPage';
import { AppShell } from '@/modules/layout/AppShell';
import { WorkstationSetupPage } from '@/modules/layout/WorkstationSetupPage';
import { SalesHistoryPage } from '@/modules/sales-history/SalesHistoryPage';
import { SettingsOpsPage } from '@/modules/settings/SettingsOpsPage';
import { UsersAdminPage } from '@/modules/users-admin/UsersAdminPage';
import { useAuthStore } from '@/stores/authStore';

const APP_ENTRY_ROUTES: readonly {
  path: string;
  any_permission: readonly string[];
}[] = [
  { path: '/app/dashboard', any_permission: ['admin.dashboard.view'] },
  {
    path: '/app/customer-statement',
    any_permission: ['admin.reports.view', 'open_accounts.payment'],
  },
  { path: '/app/cashier', any_permission: ['pos.cashier.use'] },
  { path: '/app/sales-history', any_permission: ['pos.cashier.use'] },
  { path: '/app/doctor', any_permission: ['pos.doctor.use'] },
  { path: '/app/display/waiting-room', any_permission: ['display.waiting_room.view'] },
  { path: '/app/display/medical-queue', any_permission: ['display.medical_queue.view'] },
  { path: '/app/customers', any_permission: ['customers.read'] },
  { path: '/app/catalog', any_permission: ['catalog.products.read', 'catalog.services.read'] },
  { path: '/app/inventory', any_permission: ['inventory.read'] },
  {
    path: '/app/cash',
    any_permission: ['cash.shift.open', 'cash.movement.cash_in', 'pos.cashier.use'],
  },
  { path: '/app/open-accounts', any_permission: ['open_accounts.read'] },
  { path: '/app/invoicing', any_permission: ['invoicing.cfdi.request'] },
  { path: '/app/audit', any_permission: ['audit.view'] },
  { path: '/app/otp', any_permission: ['admin.users.manage'] },
  { path: '/app/pricing', any_permission: ['pricing.rules.read'] },
  { path: '/app/clinical', any_permission: ['clinical.visits.write'] },
  { path: '/app/hospitalization', any_permission: ['hospitalization.records.read'] },
  { path: '/app/appointments', any_permission: ['appointments.read'] },
  { path: '/app/settings', any_permission: ['admin.users.manage', 'display.waiting_room.manage'] },
];

function AppIndexRedirect(): JSX.Element {
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const entry = APP_ENTRY_ROUTES.find((item) =>
    item.any_permission.some((permission) => permissions.includes(permission)),
  );
  return <Navigate to={entry?.path ?? '/app/dashboard'} replace />;
}

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/app" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/setup/workstation', element: <WorkstationSetupPage /> },
  {
    // Customer portal — isolated: own Apollo client (customer token, no workstation), own auth.
    path: '/portal',
    element: (
      <ApolloProvider client={portalApolloClient}>
        <Outlet />
      </ApolloProvider>
    ),
    children: [
      { index: true, element: <Navigate to="/portal/pets" replace /> },
      { path: 'login', element: <PortalLoginPage /> },
      {
        element: <RequireCustomerAuth />,
        children: [
          {
            element: <PortalShell />,
            children: [
              { path: 'pets', element: <PortalPetsPage /> },
              { path: 'pets/:pet_id', element: <PortalPetDetailPage /> },
              { path: 'appointments', element: <PortalAppointmentsPage /> },
              { path: 'appointments/new', element: <PortalRequestAppointmentPage /> },
              { path: 'profile', element: <PortalProfilePage /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/app',
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <AppIndexRedirect /> },
          {
            element: <RequireAuth required_permissions={['admin.dashboard.view']} />,
            children: [{ path: 'dashboard', element: <DashboardPage /> }],
          },
          {
            element: (
              <RequireAuth
                any_permission={['admin.reports.view', 'open_accounts.payment']}
              />
            ),
            children: [{ path: 'customer-statement', element: <CustomerStatementPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['pos.cashier.use']} />,
            children: [
              { path: 'cashier', element: <CashierPosPage /> },
              { path: 'sales-history', element: <SalesHistoryPage /> },
            ],
          },
          {
            element: <RequireAuth required_permissions={['pos.doctor.use']} />,
            children: [{ path: 'doctor', element: <DoctorPosPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['display.waiting_room.view']} />,
            children: [{ path: 'display/waiting-room', element: <WaitingRoomBoardPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['display.medical_queue.view']} />,
            children: [{ path: 'display/medical-queue', element: <MedicalQueueBoardPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['customers.read']} />,
            children: [
              { path: 'customers', element: <CustomersPage /> },
              { path: 'customers/:id', element: <CustomerDetailPage /> },
            ],
          },
          {
            element: <RequireAuth required_permissions={['catalog.products.read']} />,
            children: [
              { path: 'catalog', element: <CatalogIndexPage /> },
              { path: 'catalog/products', element: <ProductsPage /> },
              { path: 'catalog/categories', element: <CategoriesPage /> },
              { path: 'catalog/units', element: <UnitsPage /> },
            ],
          },
          {
            element: <RequireAuth required_permissions={['catalog.services.read']} />,
            children: [{ path: 'catalog/services', element: <ServicesPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['catalog.products.read']} />,
            children: [{ path: 'catalog/kits', element: <KitsPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['inventory.read']} />,
            children: [
              { path: 'inventory', element: <InventoryPage /> },
              { path: 'inventory/movements', element: <MovementsHistoryPage /> },
            ],
          },
          { path: 'cash', element: <CashShiftsPage /> },
          {
            element: <RequireAuth required_permissions={['open_accounts.read']} />,
            children: [{ path: 'open-accounts', element: <OpenAccountsPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['invoicing.cfdi.request']} />,
            children: [{ path: 'invoicing', element: <InvoicingPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['audit.view']} />,
            children: [{ path: 'audit', element: <AuditPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['admin.users.manage']} />,
            children: [
              { path: 'otp', element: <OtpAdminPage /> },
              { path: 'users', element: <UsersAdminPage /> },
            ],
          },
          {
            element: (
              <RequireAuth
                any_permission={['admin.users.manage', 'display.waiting_room.manage']}
              />
            ),
            children: [{ path: 'settings', element: <SettingsOpsPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['pricing.rules.read']} />,
            children: [{ path: 'pricing', element: <PriceRulesPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['clinical.visits.write']} />,
            children: [{ path: 'clinical', element: <ClinicalPendingPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['hospitalization.records.read']} />,
            children: [{ path: 'hospitalization', element: <HospitalizationPage /> }],
          },
          {
            element: <RequireAuth required_permissions={['appointments.read']} />,
            children: [{ path: 'appointments', element: <AppointmentsPage /> }],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/app" replace /> },
]);
